import { getOrdersOptions } from "@/api/marketplace/getOrders";
import { ErrorBoundary } from "@/shared/suspense/ErrorBoundary";
import { LoadingFallback } from "@/shared/suspense/LoadingFallback";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { BaseNFTCard } from "@/components/BaseNFTCard";
import CancelOfferButton from "@/components/listing/CancelOfferButton";
import {
	Divider,
	Grid,
	Group,
	Pagination,
	Stack,
	Text,
	Title,
	Tooltip,
} from "@mantine/core";
import { useState } from "react";
import { formatUnits } from "viem";

export const Route = createFileRoute("/marketplace")({
	errorComponent: ({ error }) => <ErrorBoundary error={error} />,
	pendingComponent: () => <LoadingFallback />,
	component: RouteComponent,
});

function RouteComponent() {
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 8;

	const { data, isPending } = useSuspenseQuery(
		getOrdersOptions({ page, limit: PAGE_SIZE }),
	);

	const totalOrders = data?.totalOrders || 0;
	const totalPages = Math.ceil(totalOrders / PAGE_SIZE);

	// Ensure the correct page is loaded
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const pagedOrders = data.orders ?? [];

	return (
		<>
			<Title order={2} mb="xl">
				Marketplace Orders
			</Title>
			{pagedOrders.length === 0 && (
				<Text size="lg" mb="md">
					No active offers found
				</Text>
			)}
			<Grid mb="100px">
				{pagedOrders.map((order) => {
					const nftToken = order.orderData.offer[0];
					const paymentToken = order.orderData.consideration[0];
					const price = formatUnits(BigInt(paymentToken.startAmount), 6);
					const tokenId = nftToken.identifierOrCriteria;

					return (
						<Grid.Col
							key={order.orderHash}
							span={{ xs: 12, sm: 6, md: 4, lg: 3 }}
						>
							<BaseNFTCard
								collectionAddress={nftToken.token}
								// biome-ignore lint/suspicious/noExplicitAny: <explanation>
								nft={{ tokenId } as any}
								imageHeight={300}
							>
								<Stack my="md" gap={6}>
									<div>
										<Text size="sm">
											Listed: &nbsp;
											<Tooltip
												label={new Date(
													order.startTime * 1000,
												).toLocaleString()}
												withArrow
											>
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
													}}
												>
													{new Date(order.startTime * 1000).toLocaleDateString(
														undefined,
														{ day: "2-digit", month: "short", year: "numeric" },
													)}
												</span>
											</Tooltip>
											&nbsp;→&nbsp;
											<Tooltip
												label={new Date(order.endTime * 1000).toLocaleString()}
												withArrow
											>
												<span
													style={{
														textDecoration: "underline",
														cursor: "pointer",
													}}
												>
													{new Date(order.endTime * 1000).toLocaleDateString(
														undefined,
														{ day: "2-digit", month: "short", year: "numeric" },
													)}
												</span>
											</Tooltip>
										</Text>
									</div>
									<Group justify="space-between" align="center" mt={2}>
										<Text size="md">Amount: {nftToken.endAmount}</Text>
										<Text size="md" fw={500}>
											{price} USDC
										</Text>
									</Group>
								</Stack>
								<Divider my="sm" />

								<CancelOfferButton order={order} />
							</BaseNFTCard>
						</Grid.Col>
					);
				})}
			</Grid>
			<div
				style={{
					position: "fixed",
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 100,
					background: "rgba(255,255,255,0.95)",
					padding: "1rem 0",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<Pagination
					value={page}
					onChange={handlePageChange}
					total={totalPages}
					disabled={isPending}
				/>
			</div>
		</>
	);
}
